import { useEffect, useRef, useState } from "react";
import { Search, Plus, ChevronDown, Pencil } from "lucide-react";
import { clientsApi } from "@/lib/api";
import ClientEditDialog from "@/components/ClientEditDialog";

function formatClient(c) {
  return [c.nom, c.prenom].filter(Boolean).join(" ");
}

export default function ClientSearchInput({
  value,             // selected client object or null
  onChange,          // (client) => void
  onCreateNew,       // optional callback to open "Nouveau client" modal/page
  allowEdit = true,  // show inline "Modifier" button when a client is selected
  testid = "client-search",
  placeholder = "Rechercher un client (nom, prénom)...",
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const search = (q) => {
    setLoading(true);
    clientsApi
      .list({ search: q, sort_by: "nom" })
      .then((data) => setResults(data.slice(0, 20)))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  };

  const handleInput = (e) => {
    const q = e.target.value;
    setQuery(q);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(q), 250);
  };

  const handleFocus = () => {
    setOpen(true);
    if (!results.length) search("");
  };

  const handleSelect = (c) => {
    onChange?.(c);
    setQuery("");
    setOpen(false);
  };

  const handleClear = () => {
    onChange?.(null);
    setQuery("");
    setOpen(false);
  };

  if (value) {
    return (
      <div data-testid={testid} className="space-y-1">
        <div className="flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-[var(--vidoc-bg-secondary)] border border-[var(--vidoc-border)]">
          <div className="min-w-0">
            <div className="font-semibold truncate">{formatClient(value)}</div>
            <div className="text-xs text-[var(--vidoc-text-secondary)] truncate">
              {value.numero_client ? `N° ${value.numero_client} · ` : ""}
              {value.email || value.telephone || value.ville || ""}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {allowEdit && (
              <button
                data-testid="client-search-edit"
                type="button"
                onClick={() => setEditing(true)}
                className="flex items-center gap-1 text-sm text-[var(--vidoc-primary)] font-medium px-2 py-1 rounded-lg hover:bg-white/60"
                title="Modifier le client"
              >
                <Pencil className="w-4 h-4" />
                <span className="hidden sm:inline">Modifier</span>
              </button>
            )}
            <button
              data-testid="client-search-clear"
              type="button"
              onClick={handleClear}
              className="text-sm text-[var(--vidoc-text-secondary)] font-medium px-2 py-1 rounded-lg hover:bg-white/60"
            >
              Changer
            </button>
          </div>
        </div>

        {editing && (
          <ClientEditDialog
            client={value}
            onClose={() => setEditing(false)}
            onSaved={(updated) => onChange?.(updated)}
          />
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} data-testid={testid} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--vidoc-text-tertiary)] pointer-events-none" />
        <input
          data-testid="client-search-input"
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-3 rounded-xl bg-[var(--vidoc-bg-secondary)] border border-transparent focus:border-[var(--vidoc-primary)] focus:bg-white outline-none touch-target"
        />
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--vidoc-text-tertiary)] pointer-events-none" />
      </div>

      {open && (
        <div
          data-testid="client-search-results"
          className="absolute z-30 mt-1 left-0 right-0 max-h-72 overflow-auto rounded-xl bg-white border border-[var(--vidoc-border)] shadow-lg animate-slideUp"
        >
          {onCreateNew && (
            <button
              data-testid="client-search-create-new"
              type="button"
              onClick={() => {
                setOpen(false);
                onCreateNew();
              }}
              className="w-full flex items-center gap-2 px-4 py-3 text-left text-[var(--vidoc-primary)] font-medium hover:bg-[var(--vidoc-bg-secondary)] border-b border-[var(--vidoc-border)]"
            >
              <Plus className="w-4 h-4" />
              Nouveau client
            </button>
          )}
          {loading && (
            <div className="px-4 py-3 text-sm text-[var(--vidoc-text-secondary)]">
              Recherche...
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-[var(--vidoc-text-secondary)]">
              Aucun client trouvé
            </div>
          )}
          {!loading &&
            results.map((c) => (
              <button
                key={c.id}
                data-testid={`client-search-item-${c.id}`}
                type="button"
                onClick={() => handleSelect(c)}
                className="w-full text-left px-4 py-2.5 hover:bg-[var(--vidoc-bg-secondary)] border-b border-[var(--vidoc-border)] last:border-b-0"
              >
                <div className="font-medium">{formatClient(c)}</div>
                <div className="text-xs text-[var(--vidoc-text-secondary)]">
                  {c.numero_client ? `N° ${c.numero_client} · ` : ""}
                  {c.ville || c.email || c.telephone || ""}
                </div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
